import { useCallback, useEffect, useState } from 'react';
import { api, ROL_ETIKET, type Rol } from '../lib/api';
import { FotoEkle } from './FotoEkle';
import { KameraIkon } from '../lib/ikon';
import {
  ISKELE_TIPI_ETIKET,
  ISKELE_TIPLERI,
  olcuAciklama,
  olcuMetraj,
  rolEtiket,
  metrajOzeti,
  metrajYazi,
  type IskeleTipi,
} from '@mod/domain';

/**
 * KEŞİF FORMU (devir dokumani Bolum 6.11, prototip `kesifForm()`).
 *
 * ⚠️ HICBIR ALAN ZORUNLU DEGIL — kullanicinin ozel istegi: "must olmayacak sekilde".
 *
 * COKLU OLCU MIKSI (K11): bir is emri birden cok iskele icerebilir; her satir
 * adlandirilabilir ("ön platform") ve canli toplam gosterilir.
 *
 * SERBEST ONDALIK GIRIS: ebatlar sabit deger degil (2,07 / 3,20) — stepper DEGIL,
 * sayisal klavye. Virgul de nokta da kabul edilir.
 *
 * OTOMATIK BIRIM (Bolum 10.2 #1): tum satirlarda yukseklik varsa m³, yoksa m².
 */

interface Olcu {
  ad: string;
  tip: IskeleTipi;
  en: string;
  boy: string;
  yuk: string;
  yaricap: string;
}

const BOS_OLCU: Olcu = { ad: '', tip: 'normal', en: '', boy: '', yuk: '', yaricap: '' };

const sayi = (v: string): number => {
  const n = Number.parseFloat(v.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Satır kaydedilebilir mi — tipin GEREKTİRDİĞİ alanlar dolu mu. */
const olcuTam = (o: Olcu): boolean =>
  o.tip === 'kapama'
    ? sayi(o.yaricap) > 0
    : sayi(o.en) > 0 && sayi(o.boy) > 0 && (o.tip !== 'normal' || sayi(o.yuk) > 0);

/** Formdaki satırı domain girdisine çevirir — hesap tek yerden yapılsın. */
const domaine = (o: Olcu) => ({
  ad: o.ad || null,
  tip: o.tip,
  en: o.tip === 'kapama' ? null : sayi(o.en),
  boy: o.tip === 'kapama' ? null : sayi(o.boy),
  yuk: o.tip === 'normal' ? sayi(o.yuk) : null,
  yaricap: o.tip === 'kapama' ? sayi(o.yaricap) : null,
});

export function KesifForm({ jobId, onBitti }: { jobId: string; onBitti: () => void | Promise<void> }) {
  const [olculer, setOlculer] = useState<Olcu[]>([{ ...BOS_OLCU }]);
  const [not, setNot] = useState('');
  const [ekip, setEkip] = useState<string[]>([]);
  const [calisanlar, setCalisanlar] = useState<{ id: string; ad: string; rol: Rol }[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [fotolar, setFotolar] = useState<{ fotoNo: number }[]>([]);

  /** Yüklenen keşif fotoğraflarını tazele — numaralar kanıt zincirinin parçası. */
  const fotolariYukle = useCallback(async () => {
    const d = await api
      .get<{ fotolar: { fotoNo: number; step: string }[] }>(`/isler/${jobId}`)
      .catch(() => null);
    if (d) setFotolar(d.fotolar.filter((f) => f.step === 'kesif'));
  }, [jobId]);

  useEffect(() => {
    void fotolariYukle();
  }, [fotolariYukle]);

  useEffect(() => {
    api
      .get<{ calisanlar: { id: string; ad: string; rol: Rol; sicilNo: number }[] }>('/tanimlar')
      .then((d) => setCalisanlar(d.calisanlar.filter((c) => c.sicilNo < 9000)))
      .catch(() => setCalisanlar([]));
  }, []);

  /*
   * ⚠️ HESAP @mod/domain'DEN. Bu ekran daha önce formülü KENDİ yazıyordu; aynı
   *    kuralın iki yerde durması, birinin değişip diğerinin kalması demek.
   *    Şimdi hem canlı önizleme hem sunucu aynı fonksiyonu çağırıyor.
   */
  const dolu = olculer.filter(olcuTam);
  const hesap = metrajOzeti(dolu.map(domaine));

  const guncelle = (i: number, alan: keyof Olcu, v: string) =>
    setOlculer((p) => p.map((o, k) => (k === i ? { ...o, [alan]: v } : o)));

  async function kaydet() {
    setHata(null);
    setMesgul(true);
    try {
      await api.post(`/isler/${jobId}/kesif`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        olculer: dolu.map(domaine),
        not: not.trim() || null,
        ekipIds: ekip,
      });
      await onBitti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Keşif kaydedilemedi');
      setMesgul(false);
    }
  }

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-2.5 py-2.5 text-base outline-none focus:border-navy';

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-ink-2">
          Ölçü miksi <span className="font-normal text-wait">— parçalı kurulum için birden çok</span>
        </label>
        <div className="space-y-2">
          {olculer.map((o, i) => (
            <div key={i} className="rounded-(--radius-mod) border border-line p-2.5">
              <div className="mb-2 flex gap-2">
                <input
                  className={`${gir} text-sm`}
                  placeholder="Parça adı (ops.) — ör. ana platform"
                  value={o.ad}
                  onChange={(e) => guncelle(i, 'ad', e.target.value)}
                />
                {olculer.length > 1 && (
                  <button
                    onClick={() => setOlculer((p) => p.filter((_, k) => k !== i))}
                    className="shrink-0 px-2 text-wait"
                  >
                    ✕
                  </button>
                )}
              </div>
              {/*
                İSKELE TİPİ — hangi alanların çıkacağını ve nasıl hesaplanacağını
                belirler. Tip değişince kullanılmayan alanlar TEMİZLENİR: ekranda
                görünmeyen bir değerin kaydedilmesi, sonradan tip değiştiğinde
                sessizce hesaba girmesi demek.
              */}
              <select
                className={`${gir} mb-2 text-sm`}
                value={o.tip}
                onChange={(e) => {
                  const tip = e.target.value as IskeleTipi;
                  setOlculer((p) =>
                    p.map((x, k) =>
                      k === i
                        ? {
                            ...x,
                            tip,
                            ...(tip === 'kapama'
                              ? { en: '', boy: '', yuk: '' }
                              : { yaricap: '', ...(tip === 'asma' ? { yuk: '' } : {}) }),
                          }
                        : x,
                    ),
                  );
                }}
              >
                {ISKELE_TIPLERI.map((t) => (
                  <option key={t} value={t}>
                    {ISKELE_TIPI_ETIKET[t]}
                  </option>
                ))}
              </select>

              {/* Serbest ondalık — stepper DEĞİL, sayısal klavye */}
              {o.tip === 'kapama' ? (
                <div className="flex items-center gap-2">
                  <input
                    className={`${gir} num`}
                    inputMode="decimal"
                    placeholder="yarıçap (r)"
                    value={o.yaricap}
                    onChange={(e) => guncelle(i, 'yaricap', e.target.value)}
                  />
                  <span className="num shrink-0 text-xs text-wait">π × r²</span>
                </div>
              ) : (
                <div className={`grid gap-2 ${o.tip === 'normal' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  {(o.tip === 'normal'
                    ? (['en', 'boy', 'yuk'] as const)
                    : (['en', 'boy'] as const)
                  ).map((alan) => (
                    <input
                      key={alan}
                      className={`${gir} num`}
                      inputMode="decimal"
                      placeholder={alan === 'yuk' ? 'yük' : alan}
                      value={o[alan]}
                      onChange={(e) => guncelle(i, alan, e.target.value)}
                    />
                  ))}
                </div>
              )}
              <p className="num mt-1.5 text-right text-xs text-wait">
                {olcuTam(o) ? olcuAciklama(domaine(o)) : '—'}
              </p>
            </div>
          ))}
        </div>
        <button
          onClick={() => setOlculer((p) => [...p, { ...BOS_OLCU }])}
          className="mt-2 w-full rounded-(--radius-mod) border border-dashed border-line-2 py-2.5 text-sm text-wait"
        >
          + ölçü ekle (parçalı / farklı iskele)
        </button>
      </div>

      {/*
        CANLI TOPLAM — İKİ AYRI SAYI, otomatik.
        Karışık iş tek sayıya indirgenmiyor: hacimli ve alan iskeleler ayrı
        toplanıyor (müşteri kararı 02.08.2026).
      */}
      <div className="rounded-(--radius-mod) bg-navy/5 p-3">
        <span className="num text-2xl font-semibold text-navy">{metrajYazi(hesap)}</span>
        <span className="ml-2 text-xs text-wait">toplam · otomatik</span>
        {hesap.m3 > 0 && hesap.m2 > 0 && (
          <p className="num mt-1 text-xs text-wait">
            eşdeğer {hesap.esdegerM3.toFixed(2).replace('.', ',')} m³ (m² ×4)
          </p>
        )}
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-ink-2">
          Not <span className="font-normal text-wait">— opsiyonel</span>
        </label>
        <textarea
          className={`${gir} text-sm`}
          rows={2}
          placeholder="Saha analizi, erişim zorluğu, dikkat notları…"
          value={not}
          onChange={(e) => setNot(e.target.value)}
        />
      </div>

      {/*
        KEŞİF FOTOĞRAFLARI (§6.11) — çoklu.
        ⚠️ Keşif KAYDEDİLMEDEN de yüklenebilir: saha önce fotoğrafı çeker, ölçüyü
           sonra girer. Fotoğraf zaten işe ve adıma bağlı (K8), keşif kaydının
           tamamlanmasını beklemesi için bir sebep yok.
      */}
      <div>
        <label className="mb-1.5 block text-xs font-medium text-ink-2">
          Keşif fotoğrafları{' '}
          <span className="font-normal text-wait">— zorunlu değil, çoklu</span>
        </label>
        {fotolar.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {fotolar.map((f) => (
              <span
                key={f.fotoNo}
                className="num inline-flex items-center gap-1 rounded-(--radius-mod) border border-ok-line bg-ok-bg px-2 py-1 text-xs text-ok"
              >
                <KameraIkon boyut={12} /> {f.fotoNo}
              </span>
            ))}
          </div>
        )}
        <FotoEkle
          jobId={jobId}
          step="kesif"
          etiket={fotolar.length ? 'Bir fotoğraf daha' : 'Keşif fotoğrafı çek'}
          onBitti={fotolariYukle}
        />
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-ink-2">
          Kurulum ekibi{' '}
          <span className="font-normal text-wait">— serbest seç (ekip başı zorunlu değil)</span>
        </label>
        <div className="space-y-1">
          {calisanlar.map((c) => {
            const secili = ekip.includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() =>
                  setEkip((p) => (secili ? p.filter((x) => x !== c.id) : [...p, c.id]))
                }
                className={`flex w-full items-center gap-2 rounded-(--radius-mod) border px-3 py-2.5 text-left text-sm ${
                  secili ? 'border-navy bg-navy/5' : 'border-neutral-200'
                }`}
              >
                <span className={secili ? 'text-ok' : 'text-faint'}>✓</span>
                <span className="flex-1">{c.ad}</span>
                <span className="text-xs text-wait">{rolEtiket(c.rol)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {hata && <p className="text-sm text-crit">{hata}</p>}

      <button
        disabled={mesgul}
        onClick={() => void kaydet()}
        className="w-full rounded-(--radius-mod) bg-navy px-4 py-3.5 text-base font-medium text-white disabled:opacity-40"
      >
        {mesgul ? 'Kaydediliyor…' : '✓ Keşfi Kaydet'}
      </button>
    </div>
  );
}
